import { useCallback, useEffect, useMemo, useState } from "react";

import { groupBy, isDefined } from "@bps/common-utils";
import {
  CommunicationType,
  ContactStatus,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { isMobileMinLengthReached } from "@modules/practice/screens/contact-details/shared-components/edit/contact-methods/utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";

export const useContactsWithDuplicatedMobile = ({
  originalContactId,
  type,
  value
}: {
  originalContactId: string | undefined;
  type: CommunicationType | undefined;
  value: string | undefined;
}) => {
  const { practice } = useStores();
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    if (type && value && isMobileMinLengthReached(type, value)) {
      practice
        .fetchContacts({
          filter: { phoneNumber: value, statuses: [ContactStatus.Active] }
        })
        .then(contacts => {
          const contactsWithExactMatch = contacts.results.filter(contact =>
            contact.communications.some(
              comm =>
                comm.type === CommunicationType.Mobile && comm.value === value
            )
          );

          setContacts(contactsWithExactMatch);
        });
    } else {
      setContacts([]);
    }
  }, [practice, type, value]);

  // Group contacts by type
  const groupedContacts = useMemo(
    () => groupBy(contacts, x => x.type),
    [contacts]
  );

  // Function to get contacts by type
  const getContactsByType = useCallback(
    <T extends ContactType>(type: T): Contact[] => {
      const found = groupedContacts.find(
        ([contactType]) => contactType === type
      );
      return found
        ? found[1].filter(x => isDefined(x) && x.id !== originalContactId)
        : [];
    },
    [groupedContacts, originalContactId]
  );

  // Pre-fetch common contact types
  const duplicatedPatients = useMemo(
    () => getContactsByType(ContactType.Patient),
    [getContactsByType]
  );

  const duplicatedIndividuals = useMemo(
    () => getContactsByType(ContactType.Individual),
    [getContactsByType]
  );

  const duplicatedOrganisations = useMemo(
    () => getContactsByType(ContactType.Organisation),
    [getContactsByType]
  );

  const duplicatesExist =
    duplicatedPatients.length > 0 ||
    duplicatedIndividuals.length > 0 ||
    duplicatedOrganisations.length > 0;

  return {
    duplicatesExist,
    duplicatedPatients,
    duplicatedIndividuals,
    duplicatedOrganisations
  };
};
